import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { essays } from '../data/essays';
import { ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import heroBg from '@/assets/essays-hero.jpg';

const Essays = () => {
  const lead = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "ParadoxEdge Essays",
    "url": "https://www.paradoxedge.com/essays",
    "author": { "@type": "Person", "name": "Kent Frazier" },
    "blogPost": essays.map((e) => ({
      "@type": "BlogPosting",
      "headline": e.title,
      "description": e.subtitle,
      "datePublished": e.date,
      "author": { "@type": "Person", "name": e.author },
      "url": `https://www.paradoxedge.com/essays/${e.slug}`,
    })),
  };

  return (
    <>
      <SEOHead
        title="Essays"
        description="Long-form essays from Kent Frazier on leadership, development, and the conditions in which human beings grow."
        canonicalUrl="/essays"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <ElfsightWidget />
        <main className="flex-1">
          {/* Hero - matches About/interior hero rhythm */}
          <section className="relative py-24 sm:py-32 overflow-hidden bg-foreground">
            <div className="absolute inset-0">
              <img
                src={heroBg}
                alt=""
                className="w-full h-full object-cover object-center opacity-30"
                loading="eager"
              />
            </div>
            <div className="absolute inset-0 bg-foreground/80" />
            <div
              className="absolute inset-0 pointer-events-none opacity-80"
              style={{
                background:
                  'radial-gradient(ellipse at 15% 80%, hsl(var(--primary) / 0.22) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
                backgroundSize: '120px 100%',
              }}
              aria-hidden="true"
            />

            <div className="container-mobile relative z-10 max-w-7xl mx-auto">
              <div className="grid md:grid-cols-12 gap-16 md:gap-16 items-end">
                <div
                  ref={lead.ref}
                  className={`md:col-span-7 transition-all duration-1000 ${
                    lead.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-8">
                    <span className="h-px w-12 bg-primary" aria-hidden="true" />
                    <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                      Essays
                    </p>
                  </div>
                  <h1
                    className="text-background text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-8"
                    style={{ textWrap: 'balance' }}
                  >
                    Writing on leadership and <span className="text-primary italic">technology.</span>
                  </h1>
                </div>

                <div
                  ref={tail.ref}
                  className={`md:col-span-5 md:pl-8 md:border-l md:border-background/15 transition-all duration-1000 delay-200 ${
                    tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
                    Long-form thinking
                  </p>
                  <p className="text-background/80 text-lg leading-relaxed font-normal">
                    On the work of leadership, the practice of development, and the conditions in which human beings grow.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Index */}
          <section className="py-20 sm:py-28">
            <div className="container-mobile max-w-3xl">
              <ul className="space-y-12">
                {essays.map((essay, index) => (
                  <li key={essay.slug}>
                    <Link
                      to={`/essays/${essay.slug}`}
                      className="group block"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground mb-3">
                        <span className="font-medium text-primary">Essay {index + 1}</span>
                        <span aria-hidden="true">·</span>
                        <time dateTime={essay.date}>{essay.dateDisplay}</time>
                        <span aria-hidden="true">·</span>
                        <span>{essay.readingMinutes} min read</span>
                      </div>
                      <h2 className="font-display text-3xl sm:text-4xl font-medium mb-3 group-hover:text-primary transition-colors">
                        {essay.title}
                      </h2>
                      <p className="text-foreground/90 text-lg leading-relaxed mb-4">
                        {essay.subtitle}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        Read essay
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Essays;